import { jsx as _jsx } from "react/jsx-runtime";
import { useEffectiveClinics } from '@/hooks/use-current-user';
import { useEffectiveTcGroups } from '@/hooks/use-tc-groups';
import { useTranslation } from 'react-i18next';
const SCOPE_LABEL_KEYS = {
    system: 'reporting.system',
    personal: 'reporting.personal',
    enterprise: 'reporting.scope.clinic',
    group: 'reporting.scope.group',
};
/**
 * Small pill showing who owns a template row (Personal / Clinic / Group / System).
 *
 * A shared row also names its owner when that name is resolvable — "Clinic ·
 * Becker Equine" — because anyone who can see more than one clinic's or group's
 * templates otherwise reads a column of identical "Clinic" pills. That is every
 * multi-clinic user, and now every super admin, whose list spans the region.
 * useEffectiveClinics / useEffectiveTcGroups already fan super admins out across
 * all clinics and groups, and both are cached React Query reads the surrounding
 * screens already make. An id that resolves to nothing falls back to the plain
 * scope label.
 */
export function TemplateScopeBadge({ template }) {
    const { t } = useTranslation('settings');
    const { clinics } = useEffectiveClinics();
    const { groups } = useEffectiveTcGroups();
    const { ownerType, clinicId, groupId } = template;
    let label = t(SCOPE_LABEL_KEYS[ownerType] ?? 'reporting.personal');
    if (ownerType === 'enterprise' && clinicId) {
        const clinicName = clinics.find((c) => c.clinicId === clinicId)?.clinicName;
        if (clinicName)
            label = t('reporting.scope.clinicNamed', { name: clinicName });
    }
    else if (ownerType === 'group' && groupId) {
        const groupName = groups.find((g) => g.id === groupId)?.name;
        if (groupName)
            label = t('reporting.scope.groupNamed', { name: groupName });
    }
    return (_jsx("span", { className: "bg-muted text-muted-foreground max-w-[14rem] truncate rounded-full px-1.5 py-0.5 text-[10px]", title: label, children: label }));
}
